import type { Quote } from '@/types';
import { formatRange } from '../../../../backend/src/utils/productInvestment';

/** Bedragen in het overzicht zijn hele euro's: centen leiden alleen maar af bij totalen. */
const euroFormat = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

export const euro = (cents: number) => euroFormat.format(cents / 100);

/** Kolomtotalen staan naast elkaar en moeten smal blijven: vanaf € 10.000 afgekort. */
export function euroShort(cents: number) {
  const euros = cents / 100;
  if (Math.abs(euros) >= 10_000) return `€ ${Math.round(euros / 1000)}k`;
  return euroFormat.format(euros);
}

/**
 * Een offerte met een van–tot prijs is nog een concept: de waarde is dan een
 * bandbreedte. valueMaxCents ontbreekt wanneer de offerte één bedrag heeft.
 */
export const quoteMaxValueCents = (quote: Quote) => quote.valueMaxCents ?? quote.valueCents ?? 0;

export function quotesMaxValueCents(quotes: Quote[]) {
  return quotes.reduce((sum, quote) => sum + quoteMaxValueCents(quote), 0);
}

export function euroRange(minCents: number, maxCents: number | undefined, format = euro) {
  return formatRange(minCents, maxCents, format);
}

/** Waarde van één offerte, of een streepje zolang er geen investering is ingevuld. */
export function quoteValueLabel(quote: Quote, format = euro) {
  return quote.valueCents || quote.valueMaxCents
    ? euroRange(quote.valueCents ?? 0, quote.valueMaxCents, format)
    : '—';
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function daysAgo(value: string | Date, now: number) {
  return Math.floor((now - new Date(value).getTime()) / DAY_MS);
}

export function relativeDays(value: string | Date, now: number) {
  const days = daysAgo(value, now);
  if (days <= 0) return 'vandaag';
  if (days === 1) return 'gisteren';
  return `${days} d geleden`;
}

/** Initialen van de dealeigenaar; valt terug op een streepje zodat de kolom blijft uitlijnen. */
export function ownerInitials(quote: Quote) {
  const name = quote.dealOwnerName?.trim();
  if (!name) return '–';
  const parts = name.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
  return letters.toUpperCase();
}
